import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  cn,
  FormField,
  Input,
  Modal,
} from '@playerbase/ui';
import type { BuildMarker, GameDevStage } from '@playerbase/types';

import { STAGE_BADGE_CLASSES } from '../../../games/stage-colour-classes.js';

export type BuildFormValues = {
  label: string;
  releasedAt: string;
  stageId: string;
  notes: string | null;
};

type BuildFormModalProps = {
  open: boolean;
  mode: 'create' | 'edit';
  marker: BuildMarker | null;
  devStages: GameDevStage[];
  onCancel: () => void;
  onSubmit: (values: BuildFormValues) => Promise<void>;
};

// HTML datetime-local inputs want a local-naive `YYYY-MM-DDTHH:MM` string.
// We convert to/from ISO at the boundary so the rest of the codebase keeps
// using ISO timestamps.
function toLocalInput(iso: string | null): string {
  const date = iso !== null ? new Date(iso) : new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function toIso(local: string): string {
  return new Date(local).toISOString();
}

export function BuildFormModal({
  open,
  mode,
  marker,
  devStages,
  onCancel,
  onSubmit,
}: BuildFormModalProps): JSX.Element {
  const firstStageId = devStages[0]?.id ?? '';
  const [label, setLabel] = useState<string>(marker?.label ?? '');
  const [releasedAt, setReleasedAt] = useState<string>(toLocalInput(marker?.releasedAt ?? null));
  const [stageId, setStageId] = useState<string>(marker?.stageId ?? firstStageId);
  const [notes, setNotes] = useState<string>(marker?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reset form whenever the modal opens with new context.
  useEffect(() => {
    if (!open) return;
    setLabel(marker?.label ?? '');
    setReleasedAt(toLocalInput(marker?.releasedAt ?? null));
    setStageId(marker?.stageId ?? firstStageId);
    setNotes(marker?.notes ?? '');
    setError(null);
    setBusy(false);
  }, [open, marker, firstStageId]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = label.trim();
    if (trimmed.length === 0) {
      setError('Build label is required.');
      return;
    }
    if (stageId === '') {
      setError('Pick a stage.');
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        label: trimmed,
        releasedAt: toIso(releasedAt),
        stageId,
        notes: notes.trim() === '' ? null : notes,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onCancel} busy={busy} labelledById="build-form-title">
      <Card>
        <Card.Header>
          <Card.Title id="build-form-title">
            {mode === 'create' ? 'Log a new build' : 'Edit build'}
          </Card.Title>
          <Card.Description>
            {mode === 'create'
              ? 'Records when the build shipped, what stage it sits in, and any notes for the timeline.'
              : 'Edit the label, shipped time, stage, or notes. Stage changes are written to the audit log.'}
          </Card.Description>
        </Card.Header>
        <Card.Content>
          <form
            onSubmit={(e) => { void handleSubmit(e); }}
            className="flex flex-col gap-4"
            noValidate
          >
            {error !== null && <Alert tone="danger">{error}</Alert>}

            <FormField>
              <FormField.Label>Build label</FormField.Label>
              <FormField.Control>
                <Input
                  autoFocus
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="v0.4.2"
                  className="w-full"
                />
              </FormField.Control>
              <FormField.HelpText>
                Use whatever convention your team likes — version numbers, build IDs, branch names.
              </FormField.HelpText>
            </FormField>

            <FormField>
              <FormField.Label>Shipped time</FormField.Label>
              <FormField.Control>
                {/* Use a raw <input> rather than the Input atom: the atom's
                    `display: flex` collides with the WebKit shadow layout
                    and parks the calendar indicator next to the value text
                    instead of at the trailing edge. Style is otherwise the
                    same as the Input atom's default. */}
                <input
                  type="datetime-local"
                  value={releasedAt}
                  onChange={(e) => setReleasedAt(e.target.value)}
                  className="block h-8 w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-text transition-colors duration-150 placeholder:text-text-dim focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50"
                />
              </FormField.Control>
              <FormField.HelpText>
                Defaults to now. Backdate forgotten builds so the timeline stays accurate.
              </FormField.HelpText>
            </FormField>

            <FormField>
              <FormField.Label>Stage</FormField.Label>
              <FormField.Control>
                <div className="flex flex-wrap gap-1.5">
                  {devStages.map((stage) => (
                    <button
                      key={stage.id}
                      type="button"
                      onClick={() => setStageId(stage.id)}
                      className={cn(
                        'rounded-sm border px-2 py-1 text-[11px] font-mono uppercase tracking-wider transition-opacity',
                        STAGE_BADGE_CLASSES[stage.colour],
                        stage.id === stageId ? 'opacity-100 ring-1 ring-accent' : 'opacity-60 hover:opacity-100',
                      )}
                    >
                      {stage.label}
                    </button>
                  ))}
                </div>
              </FormField.Control>
            </FormField>

            <FormField>
              <FormField.Label>Release notes (optional)</FormField.Label>
              <FormField.Control>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Changelog or context for the timeline."
                />
              </FormField.Control>
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={busy}>
                {busy ? 'Saving…' : mode === 'create' ? 'Log build' : 'Save changes'}
              </Button>
            </div>
          </form>
        </Card.Content>
      </Card>
    </Modal>
  );
}
